import React, { useEffect, useRef, useState } from 'react';
import { Loader2, SlidersHorizontal, X } from 'lucide-react';
import { toast } from 'sonner';
import { withBackend, toThumbnailUrl } from '../utils/api';

// Layout allowlist for native PPTX masters: the owner unticks layouts the AI
// must never use (decorative dividers, display-typography art). Human
// curation is the reliability guarantee heuristics can't provide — designers
// know in seconds which layouts are content layouts.
export default function NativeLayoutPickerModal({ template, userId, onClose, onSaved }) {
  const layouts = (template?.layouts || []).filter(l => (l.placeholders || []).length > 0);
  const [disabled, setDisabled] = useState(() => new Set(template?.disabled_layouts || []));
  const [saving, setSaving] = useState(false);

  // Escape closes, and lock body scroll behind the overlay. onClose goes
  // through a ref so parent re-renders (inline arrow prop) don't re-register
  // the listener.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onCloseRef.current?.(); };
    window.addEventListener('keydown', handler);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = prev;
    };
  }, []);

  const toggle = (idx) => {
    setDisabled(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const enabledCount = layouts.filter(l => !disabled.has(l.layout_index)).length;

  const save = async () => {
    if (enabledCount === 0) {
      toast.error('At least one layout must stay enabled.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(withBackend(`/api/native-templates/${template.id}/layouts`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, disabled_layouts: [...disabled] }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save layout settings');
      toast.success('Layout settings saved');
      onSaved?.(template.id, [...disabled]);
      onClose?.();
    } catch (err) {
      toast.error(err.message || 'Failed to save layout settings');
    } finally {
      setSaving(false);
    }
  };

  if (!template) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Layouts for ${template.name}`}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-4xl flex-col rounded-2xl border border-border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border/60 px-6 py-4">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 truncate text-base font-semibold text-foreground">
              <SlidersHorizontal className="h-4 w-4 text-accent" aria-hidden="true" />
              {template.name} — layouts the AI may use
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Untick decorative dividers and display-typography layouts. The AI only plans slides
              onto ticked layouts. {enabledCount} of {layouts.length} enabled.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid flex-1 grid-cols-2 gap-3 overflow-y-auto p-6 sm:grid-cols-3 lg:grid-cols-4">
          {layouts.map((l) => {
            const off = disabled.has(l.layout_index);
            return (
              <button
                key={l.layout_index}
                type="button"
                onClick={() => toggle(l.layout_index)}
                aria-pressed={!off}
                title={off ? 'Excluded — click to allow' : 'Allowed — click to exclude'}
                className={`group relative overflow-hidden rounded-lg border text-left transition-all ${
                  off
                    ? 'border-border opacity-45 grayscale'
                    : 'border-emerald-500/60 ring-1 ring-emerald-500/30'
                }`}
              >
                <div className="aspect-video w-full bg-slate-100 dark:bg-slate-800">
                  {l.thumbnail_url ? (
                    <img
                      src={toThumbnailUrl(l.thumbnail_url)}
                      alt={l.name || `Layout ${l.layout_index}`}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-[10px] text-muted-foreground">
                      No preview
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between gap-1 px-2 py-1">
                  <span className="truncate text-[11px] text-foreground">
                    {l.name || `Layout ${l.layout_index}`}
                  </span>
                  <span className={`shrink-0 rounded px-1 text-[9px] font-bold ${
                    off ? 'bg-muted text-muted-foreground' : 'bg-emerald-500/15 text-emerald-600'
                  }`}
                  >
                    {off ? 'OFF' : 'ON'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border/60 px-6 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Save layout settings
          </button>
        </div>
      </div>
    </div>
  );
}
